/**
 * Der Historien-Block „Kennen wir schon?" — EINE Komponente fuer BEIDE Orte (R1.2 + R1.3).
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 2.3 (Anfragen-Drawer), § 2.5 (Kunden-Profil:
 * die eigene Historie steht VOR allen Fremddaten), § 3.2 (was MAESTRO hat, aber nie erzaehlt).
 * Entscheidung P-7: was wir selbst erlebt haben, schlaegt jede Registerauskunft.
 *
 * ── Diese Datei RENDERT nur ─────────────────────────────────────────────────────────────────
 * Welcher Satz bei welcher Datenlage erscheint, entscheidet `kundenhistorieAnzeige()` aus
 * `@maestro/kundenhistorie-anzeige` — eine reine Funktion, getestet in
 * `apps/api/test/kundenhistorie-anzeige.test.ts`. Eine Ratsche im selben Test haelt fest, dass
 * diese Datei die Regel aufruft, statt sie nachzubauen, und dass hier keine Wertung entsteht.
 *
 * Seit 06.08.2026 hat `apps/web` einen eigenen Test-Runner. `apps/web/test/kunden-profil-reihenfolge.test.tsx`
 * prueft R1.3 am GERENDERTEN Baum (Historie als erster Block derselben Spalte) — der Test in
 * `apps/api/test/` konnte dafuer nur Zeichenpositionen im Quelltext vergleichen.
 *
 * Und EINE Komponente fuer beide Orte, nicht zwei: zwei Komponenten waeren zwei Darstellungen
 * derselben Historie, und beide saehen fuer sich plausibel aus. `ort` steuert ausschliesslich die
 * Ueberschrift und die Umrandung — die Saetze sind Wort fuer Wort dieselben.
 *
 * ── Kein Klappmuster, bewusst ────────────────────────────────────────────────────────────────
 * Die Nachbarkarte „Wer fragt hier an?" (R0.9) ist aufklappbar und standardmaessig zu, weil sie
 * viele Angaben mit je einer Herkunftszeile traegt. Dieser Block sind hoechstens sechs kurze
 * Zeilen — und der ganze Zweck von R1.2 ist, dass die Antwort auf „Kennen wir schon?" am
 * Entscheidungsort SICHTBAR ist. Sie hinter einen Klick zu legen, waere genau der Befund B1
 * („die Daten liegen da, nur nicht dort, wo entschieden wird") in klein. Dass hier kein Knopf
 * steht, ist deshalb kein Versaeumnis: es gibt an dieser Stelle nichts auszuloesen.
 *
 * ── Der Block ruft NICHTS extern ab ─────────────────────────────────────────────────────────
 * Er liest `GET /api/inquiries/:id/historie` bzw. `GET /api/customers/:id/historie` (R1.1) — reine
 * Leserouten ueber die eigene Datenbank, ohne Fremdaufruf und ohne Claim. Das Ansehen einer
 * Anfrage erzeugt weiterhin keine Last gegen das geteilte 50/h-Budget.
 *
 * ── Bewusst NICHT enthalten ─────────────────────────────────────────────────────────────────
 * Betraege (Begruendung in `@maestro/kundenhistorie-anzeige`: das Kunden-Profil zeigt sie oben
 * bereits ueber eine andere Grundmenge), ein Score, eine Ampel, eine Prognose (§ 6.1) und ein
 * „Stand vom …" (die Historie ruft nichts ab, sie zaehlt im Augenblick des Aufrufs).
 */
import { kundenhistorieAnzeige, type HistorieNutzlast, type HistorieOrt } from "@maestro/kundenhistorie-anzeige";
import { useApi } from "../hooks/useApi";
import { LoadingRows } from "./components";

/** Die beiden Umrandungen — die Anfragen-Detailseite setzt kompakte Karten, das Kunden-Profil
 *  grosszuegigere. */
const RAHMEN: Record<HistorieOrt, string> = {
  anfragen_detail: "rounded-xl border border-surface-variant bg-surface-container-lowest px-3.5 py-2.5 dark:border-white/10 dark:bg-night-card",
  kunden_profil: "rounded-xl border border-outline-variant/30 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-surface-variant/10",
};

export function KundenhistorieBlock({ pfad, ort }: { pfad: string | null; ort: HistorieOrt }) {
  const q = useApi<HistorieNutzlast | null>(pfad);
  const block = kundenhistorieAnzeige({ historie: q.data, ort });

  if (!pfad) return null;

  if (!block.sichtbar) {
    if (q.loading) {
      return (
        <div className={RAHMEN[ort]}>
          <LoadingRows rows={1} />
        </div>
      );
    }
    // Scheitert der Abruf, verschwindet der Block NICHT stillschweigend — eine verschwundene
    // Flaeche erklaert nichts (dieselbe Lehre wie „Modul aus" in R0.7). Der Satz sagt etwas ueber
    // DIESEN Abruf der eigenen API, nicht ueber den Kunden.
    if (q.error) {
      return (
        <div className={RAHMEN[ort]}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">{block.titel}</p>
          <p className="mt-0.5 font-body-sm text-[12px] text-error">Historie konnte nicht geladen werden: {q.error}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className={RAHMEN[ort]}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">{block.titel}</p>

      {/* Die eine Aussage, die auf den ersten Blick sitzen muss (R1.2). */}
      <p className="mt-0.5 font-body-sm text-[13px] font-semibold text-on-surface dark:text-inverse-on-surface">
        {block.kurzinfo}
      </p>

      {block.zeilen.length > 0 && (
        <ul className="mt-2 space-y-1">
          {block.zeilen.map((zeile) => (
            <li key={zeile} className="font-body-sm text-[12.5px] text-on-surface dark:text-inverse-on-surface">
              {zeile}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 font-body-sm text-[11px] leading-relaxed text-on-surface-variant/70">{block.fussnote}</p>
      {block.bezugSatz && <p className="mt-0.5 font-body-sm text-[11px] text-on-surface-variant/60">{block.bezugSatz}</p>}
    </div>
  );
}
